import { Book, ExternalLink, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const BOOK_IMAGE = "/api/static/uploads/ebook_cover.jpg";

const BookPromoPage = () => {
  return (
    <div className="min-h-screen pt-20 bg-[#0a0a0a]" data-testid="book-promo-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Book Image */}
            <div className="relative group order-2 lg:order-1">
              <div className="aspect-[2/3] overflow-hidden bg-[#1c1c1c] shadow-2xl">
                <img
                  src={BOOK_IMAGE}
                  alt="The Art of Luxury Fashion by Akeem Feiton"
                  className="w-full h-full object-contain"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -right-6 w-full h-full border border-[#C41E3A]/30 -z-10" />
              <div className="absolute -top-6 -left-6 w-full h-full border border-white/10 -z-10" />
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 mb-6">
                <Book className="w-5 h-5 text-[#C41E3A]" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#C41E3A]">Featured Book</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-serif italic text-white mb-6 leading-tight">
                The Art of Luxury Fashion
              </h1>
              
              <p className="text-2xl text-gray-300 font-serif italic mb-8">
                by Akeem Feiton
              </p>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-8">
                {[1, 2, 3, 4, 5].map((num) => (
                  <Star key={`star-${num}`} className="w-5 h-5 fill-[#C41E3A] text-[#C41E3A]" />
                ))}
                <span className="text-gray-400 ml-2">5.0 · Bestseller</span>
              </div>

              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                A comprehensive journey through the world of haute couture and luxury fashion. 
                This definitive guide explores the rich history, exquisite craftsmanship, and 
                lasting cultural impact of the world's most prestigious fashion houses.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://books.apple.com/us/book/the-art-of-luxury-fashion/id6479809621" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="get-book-btn"
                >
                  <Button className="btn-luxury btn-luxury-accent gap-2 w-full sm:w-auto">
                    <ExternalLink className="w-4 h-4" />
                    Get on Apple Books
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#121212]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif italic text-white mb-12 text-center">
            Inside the Book
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                id: 'heritage',
                title: 'Heritage & History',
                description: 'Explore the origins and evolution of the world\'s most iconic fashion houses, from their humble beginnings to global dominance.'
              },
              {
                id: 'craftsmanship',
                title: 'Craftsmanship',
                description: 'Discover the meticulous artistry behind luxury fashion, from hand-stitched leather goods to haute couture gowns.'
              },
              {
                id: 'cultural',
                title: 'Cultural Impact',
                description: 'Understand how luxury fashion has shaped and been shaped by cultural movements, art, and society throughout history.'
              }
            ].map((item) => (
              <div key={item.id} className="p-8 border border-white/10 bg-[#1c1c1c]">
                <h3 className="text-xl font-serif text-white mb-4">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Brands in Book */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif italic text-white mb-8">
            Featured Houses
          </h2>
          <p className="text-gray-400 mb-12">
            The book features in-depth profiles of the most influential fashion houses in history.
          </p>

          <div className="flex flex-wrap justify-center gap-8 md:gap-12">
            {['Louis Vuitton', 'Prada', 'Gucci', 'Hermès', 'Chanel'].map((brand) => (
              <span key={brand} className="text-2xl md:text-3xl font-serif italic text-gray-500 hover:text-white transition-colors cursor-default">
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#121212]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif italic text-white mb-6">
            Begin Your Fashion Education
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Available now on Apple Books. Start your journey into the world of luxury fashion.
          </p>
          <a 
            href="https://books.apple.com/us/book/the-art-of-luxury-fashion/id6479809621" 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="cta-book-btn"
          >
            <Button className="btn-luxury btn-luxury-primary">
              Get Your Copy
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default BookPromoPage;
